import { gql } from "urql";

// Note: UB batch claim line sort orders need to remain consistent with ReportingService:
const BATCH_CLAIM_LINE_SORT =
  "orderBy: [LINE_NUMBER_ASC, REV_CODE_ASC, SERVICE_DATE_ASC, ID_ASC]";

const providerFragment = gql`
  fragment providerFragment on Provider {
    id
    name
    npi
    practiceType
    spec
  }
`;

// Can admins even be assigned??
const batchClaimLineItemFragment = gql`
  fragment batchClaimLineItemFragment on BatchClaimLineItem {
    id
    dateOfServiceStart
    dateOfServiceEnd
    procedureCode
    revCode
    itemNumber
    description
    units
    unitCharge
    batchClaimLineId
    authorId
    createdAt
    updatedAt
    alaRowId
    ibLineId
    lineNumber
    computedValues: batchClaimLineItemComputedValueById {
      id
      determinedAdjustedAllowedAmount
      determinedDiscrepantAmount
      determinedAdjustedBilledAmount
    }
    batchClaimLineItemNnIbLines {
      nodes {
        id
        nnIbLineId
        nnIbLine {
          id
          fingerprint
        }
      }
    }
  }
`;

const reviewedAuditFindingFragment = gql`
  fragment reviewedAuditFindingFragment on ReviewedAuditFinding {
    id
    accepted
    deleted
    createdAt
    updatedAt
    rationale
  }
`;

const auditFindingReviewFragment = gql`
  fragment auditFindingReviewFragment on AuditFindingReview {
    id
    createdAt
    # needed for update cache
    reviewId
    originalId
  }
`;

// n.b. this is intentionally light-weight as called in nested resolver -- see note at graphql_api aliasFields.js
const versionAuditFindingFragment = gql`
  fragment versionAuditFindingFragment on AuditFinding {
    id
    improperPaymentUnitsCharged
    improperPaymentCost
    improperPaymentReason
    needsReview
    auditorReviewed
    auditorComment
    auditType
    superseded
    auditFindingRuleType
    auditFindingSeedType
    confidence
    isOriginal
    isActive
    accepted
    createdAt
    # needed for updating cache
    batchClaimLineId
    batchClaimId
    batchClaimLineItemAudit {
      id
      batchClaimLineItem {
        ...batchClaimLineItemFragment
      }
    }
  }
  ${batchClaimLineItemFragment}
`;

const auditFindingFragment = gql`
  fragment auditFindingFragment on AuditFinding {
    id
    improperPaymentUnitsCharged
    improperPaymentCost
    improperPaymentReason
    needsReview
    auditorReviewed
    auditorComment
    auditType
    superseded
    auditFindingRuleTypeByAuditFindingRuleType {
      type
      description
      displayName
    }
    auditFindingRuleType
    auditFindingSeedType
    confidence
    isOriginal
    isActive
    accepted
    createdAt
    metaDataNcciMueOhpMueValue
    metaDataNcciMueAjudicationValue
    metaDataNcciPtpModifier
    metaDataNcciPtpCode
    metaDataEmCode
    metaDataEmOriginalLevel
    metaDataEmCorrectLevel
    metaDataAocPrimaryCode
    metaDataPricingCorrectPrice
    metaDataUnitsCorrectUnits
    discrepantAmount # At the auditFindings level there is no determined vs potential because this calculates the number on that finding
    reviewedAuditFinding: latestReview {
      ...reviewedAuditFindingFragment
    }
    batchClaimLineItemAudit {
      id
      batchClaimLineItem {
        ...batchClaimLineItemFragment
      }
    }
    reportedFindings {
      totalCount
      nodes {
        id
        auditFindingReportId
      }
    }
    # needed for updating cache
    batchClaimLineId
    batchClaimId
  }
  ${batchClaimLineItemFragment}
  ${reviewedAuditFindingFragment}
`;

const batchClaimDocumentationFragment = gql`
  fragment batchClaimDocumentationFragment on BatchClaim {
    id
    auditDocumentation {
      id
      subFile
      fileName
      process
      size
      signedUrl
      uploadConfirmedAt
      __typename
    }
  }
`;

const auditFindingReviewsFragment = gql`
  fragment auditFindingReviewsFragment on AuditFinding {
    id
    reviews: auditFindingReviewsByOriginalId(orderBy: CREATED_AT_DESC) {
      totalCount
      nodes {
        ...auditFindingReviewFragment
        auditFinding: review {
          ...auditFindingFragment
        }
      }
    }
  }
  ${auditFindingReviewFragment}
  ${auditFindingFragment}
`;

// N.b. createdBy lives on this shared fragment (not the lighter auditFindingFragment) so
// the workstation calls it once per finding via the batch-claim fragments, and the
// claim workspace UB tab no longer fires a per-finding query just to select it.
// The resolver is DataLoader-batched to two indexed PK lookups regardless of count.
// See ALA-6228 / ALA-5361. Selected inline elsewhere (e.g. findingsTable.tsx) via
// ...auditFindingFragment when only the lighter shape is needed.
const auditFindingWithReviewFragment = gql`
  fragment auditFindingWithReviewFragment on AuditFinding {
    ...auditFindingFragment
    ...auditFindingReviewsFragment
    original: auditFindingReviewByReviewId {
      ...auditFindingReviewFragment
      auditFinding: original {
        ...auditFindingFragment
        ...auditFindingReviewsFragment
      }
    }
    createdBy {
      __typename
      ... on CreatedByUser {
        id
        firstName
        lastName
        userType
      }
      ... on CreatedByAutomation {
        id
        kind
        name
      }
    }
  }
  ${auditFindingReviewsFragment}
  ${auditFindingFragment}
  ${auditFindingReviewFragment}
`;

const findingV2Fragment = gql`
  fragment findingV2Fragment on FindingV2 {
    id
    accepted
    autodorFindingId
    autodorFindingS3Key
    batchClaimLineId
    confidence
    createdAt
    improperPaymentCost
    improperPaymentReason
    improperPaymentUnitsCharged
    isActive
    isOriginal
    metaDataAocPrimaryCode
    metaDataEmCode
    metaDataEmCorrectLevel
    metaDataEmOriginalLevel
    metaDataNcciMueAdjudicationValue
    metaDataNcciMueOhpMueValue
    metaDataNcciPtpCode
    metaDataNcciPtpModifier
    metaDataPricingCorrectPrice
    metaDataUnitsCorrectUnits
    needsReview
    ruleType
    seedType
    superseded
    updatedAt
    createdBy {
      __typename
      ... on CreatedByUser {
        id
        firstName
        lastName
        userType
      }
      ... on CreatedByAutomation {
        id
        kind
        name
      }
    }
    latestReview {
      authorId
      createdAt
      rationale
    }
    batchClaimLineItemAudit {
      batchClaimLineItem {
        revCode
        batchClaimLineId
        ibLineId
        alaRowId
      }
    }
  }
`;

const batchClaimLineDataFragment = gql`
  fragment batchClaimLineDataFragment on BatchClaimLine {
    id
    lineNumber
    procedureCode
    procedureDescription
    revCode
    revDescription
    unitAllowableCharge
    unitCharge
    units
    paymentRate
    computedValues: batchClaimLineComputedValueById {
      id
      determinedDiscrepantAmount
      determinedAdjustedAllowedAmount
      determinedAdjustedBilledAmount
    }
    diag1
    diag1Description
    diag2
    diag2Description
    diag3
    diag3Description
    diag4
    diag4Description
  }
`;

const batchClaimLineFragment = gql`
  fragment batchClaimLineFragment on BatchClaimLine {
    ...batchClaimLineDataFragment
    auditFindings(filter: { and: [{ isActive: { equalTo: true } }] }) {
      totalCount
      nodes {
        id
        ...auditFindingWithReviewFragment
      }
    }
    batchClaimLineItems {
      nodes {
        ...batchClaimLineItemFragment
      }
    }
  }
  ${batchClaimLineItemFragment}
  ${batchClaimLineDataFragment}
  ${auditFindingWithReviewFragment}
`;

const batchClaimState = gql`
  fragment batchClaimState on BatchClaim {
    batchClaimState: batchClaimStateById {
      appealed
      claimAuditType
      clientSignOffClaimState
      determinedAdjustedAllowedAmount
      determinedClientSavings
      determinedDiscrepantAmount
      disputedFindingClaimState
      documentationClaimState
      id
      invoiceClaimState
      isProviderActive
      isRealized
      negotiationClaimState
      onHoldClaimState
      payDeny
      techDenied
      potentialAdjustedAllowedAmount
      potentialClientSavings
      potentialDiscrepantAmount
      providerSignOffClaimState
      reported
      workflowClaimState
    }
    id
  }
`;

const adminAndAuditorBatchClaimStage = gql`
  fragment adminAndAuditorBatchClaimStage on BatchClaim {
    batchClaimState: batchClaimStateById {
      id
      stage {
        description
        id
        labelOrder
        name
        type
      }
    }
    id
  }
`;

// defaultclaimListQueryData is the return values from claimList that is shared and same for all user types
//  - leaves out:
//    - duedates
//    - assignees
const defaultclaimListQueryData = gql`
  fragment defaultclaimListQueryData on BatchClaim {
    disputed
    id
    icn
    ...batchClaimState # stats like enumclaimsStates, reported, det/pot clientSavings, discrepantAmount, & adjustedAllowedAmount live in batchClaimState
    dateOfServiceStart
    dateOfServiceEnd
    dateAdmit
    paymentRate
    dateDischarge
    amountCharged
    amountReimbursed
    claimType
    patientDob
    createdAt
    provider {
      ...providerFragment
    }
    batchClaimDocuments {
      totalCount
    }
    activeBlockers {
      slug
      name
      stage {
        slug
        name
      }
    }
  }
  ${providerFragment}
  ${batchClaimState}
`;

// Extends the defaultclaimListQueryData to match Admin/Auditor users:
//  - leaves out nothing
//  - adds in:
//    - duedates
//    - assignees
const adminAndAuditorClaimListQueryData = gql`
  fragment claimListDataFragment on BatchClaim {
    id
    permissions
    actionPermissions {
      editStage
      createFinding
      editFinding
      createBlocker
      editBlocker
      deleteBlocker
      bypassOrganizationAdmin
    }
    ...defaultclaimListQueryData
    ...adminAndAuditorBatchClaimStage
    batchClaimState: batchClaimStateById {
      id
      claimAuditGroup {
        id
        name
      }
    }
    activeAssignees {
      nodes {
        id
        firstName
        lastName
      }
    }
    activeDueDate: batchClaimStateById {
      id
      dueDate
    }
    activeAuditStartDate: batchClaimStateById {
      id
      auditStartDate
    }
  }
  ${defaultclaimListQueryData}
  ${adminAndAuditorBatchClaimStage}
`;

// Extends the defaultclaimListQueryData to match Payer/Provider users:
const payerAndProviderClaimListQueryData = gql`
  fragment claimListDataFragment on BatchClaim {
    id
    permissions
    actionPermissions {
      editStage
      createFinding
      editFinding
      createBlocker
      editBlocker
      deleteBlocker
      bypassOrganizationAdmin
    }
    icn
    batchClaimState: batchClaimStateById {
      claimAuditType
      clientSignOffClaimState
      determinedAdjustedAllowedAmount
      determinedClientSavings
      determinedDiscrepantAmount
      disputedFindingClaimState
      documentationClaimState
      id
      invoiceClaimState
      isProviderActive
      isRealized
      negotiationClaimState
      onHoldClaimState
      payDeny
      potentialAdjustedAllowedAmount
      potentialClientSavings
      potentialDiscrepantAmount
      providerSignOffClaimState
      reported
      stage {
        description
        id
        labelOrder
        name
        type
        phase {
          description
          id
          labelOrder
          name
        }
      }
      workflowClaimState
    }
    dateOfServiceStart
    dateOfServiceEnd
    dateAdmit
    paymentRate
    dateDischarge
    amountCharged
    amountReimbursed
    claimType
    patientDob
    createdAt
    provider {
      ...providerFragment
    }
    batchClaimDocuments {
      totalCount
    }
  }
  ${providerFragment}
`;

/**
 * README (KY Feb, 2023 EN-73):
 *  This fragment is designed to be called within workstation queries NOT the claimList search queries (for those use the defaultclaimListQueryData fragment)
 *  This is because this has the 'batchClaimDocumentationFragment' which uses a cuxstom resolver that returns signedUrls for uploaded documents
 *  when 'batchClaimDocumentationFragment' is called many times for multiple claims within the claimList search queries we see huge performance hits.
 *
 *  --> Therefore it is advised to use this fragment only when we explicitly need the results of the 'auditDocumentation' query such as signedUrl
 *      which is needed in the workstation for example
 */
const batchClaimDataFragment = gql`
  fragment batchClaimDataFragment on BatchClaim {
    id
    permissions
    actionPermissions {
      bypassOrganizationAdmin
      claimId
      createBlocker
      createFinding
      currentStageId
      deleteBlocker
      editBlocker
      editFinding
      editStage
    }
    icn
    ...batchClaimState # stats like enumclaimsStates, reported, det/pot clientSavings, discrepantAmount, & adjustedAllowedAmount live in batchClaimState
    ...batchClaimDocumentationFragment # uses the custom gql resolver 'auditDocumentation' enabling the return of the signedUrl -- (this is a relativley slow query)
    dateOfServiceStart
    dateOfServiceEnd
    dateAdmit
    dateDischarge
    amountCharged
    amountReimbursed
    computationStrategy
    paymentRate
    claimType
    clientGroupName
    clientLineOfBusiness
    insurer {
      name
    }
    patient {
      name
    }
    patientDob
    patientGender
    createdAt
    provider {
      ...providerFragment
    }
  }
  ${batchClaimDocumentationFragment}
  ${providerFragment}
  ${batchClaimState}
`;

const adminAndAuditorWorkstationBatchClaimFragment = gql`
  fragment adminAndAuditorWorkstationBatchClaimFragment on BatchClaim {
    id
    ...batchClaimDataFragment
    ...adminAndAuditorBatchClaimStage
    activeDueDate: batchClaimStateById {
      id
      dueDate
    }
    activeAuditStartDate: batchClaimStateById {
      id
      auditStartDate
    }
    activeAssignees {
      nodes {
        id
        firstName
        lastName
      }
    }
    provider {
      ...providerFragment
      auditorUsers {
        nodes {
          id
        }
      }
    }
    claimAuditType
    batchClaimLines(${BATCH_CLAIM_LINE_SORT}) {
      nodes {
        ...batchClaimLineFragment
      }
    }
    ubClaimFindings: auditFindings(
      filter: {
        and: [
          { isOriginal: { equalTo: true } }
          { auditFindingSeedType: { equalTo: UB_CLAIM } }
        ]
      }
    ) {
      nodes {
        ...auditFindingWithReviewFragment
        __typename
      }
      __typename
    }
    ## all audit findings for the claim
    auditFindings {
      nodes {
        ...auditFindingFragment
      }
      totalCount
    }
  }
  ${batchClaimDataFragment}
  ${adminAndAuditorBatchClaimStage}
  ${batchClaimLineFragment}
  ${providerFragment}
  ${auditFindingWithReviewFragment}
  ${auditFindingFragment}
`;

// The provider workstation Query only returns UB_CLAIM findings while Payer returns all findings
const providerBatchClaimWorkstationFragment = gql`
  fragment providerBatchClaimWorkstationFragment on BatchClaim {
    id
    ...batchClaimDataFragment
    batchClaimState: batchClaimStateById {
      stage {
        description
        id
        labelOrder
        name
        type
        phase {
          description
          id
          labelOrder
          name
        }
      }
    }
    batchClaimLines(${BATCH_CLAIM_LINE_SORT}) {
      nodes {
        ...batchClaimLineDataFragment
        auditFindings(
          filter: {
            and: [
              { isActive: { equalTo: true } }
              { auditFindingSeedType: { equalTo: UB_CLAIM } }
            ]
          }
        ) {
          totalCount
          nodes {
            id
            ...auditFindingWithReviewFragment
          }
        }
      }
    }
    ubClaimFindings: auditFindings(
      filter: {
        and: [
          { isOriginal: { equalTo: true } }
          { auditFindingSeedType: { equalTo: UB_CLAIM } }
        ]
      }
    ) {
      nodes {
        ...auditFindingWithReviewFragment
        __typename
      }
      __typename
    }
    # ONLY UB_CLAIM audit findings for the claim
    auditFindings(filter: { auditFindingSeedType: { equalTo: UB_CLAIM } }) {
      nodes {
        ...auditFindingFragment
      }
      totalCount
    }
  }
  ${batchClaimDataFragment}
  ${batchClaimLineDataFragment}
  ${auditFindingFragment}
  ${auditFindingWithReviewFragment}
`;

const payerBatchClaimWorkstationFragment = gql`
  fragment payerBatchClaimWorkstationFragment on BatchClaim {
    ...batchClaimDataFragment
    provider {
      ...providerFragment
    }
    claimAuditType
    batchClaimState: batchClaimStateById {
      stage {
        description
        id
        labelOrder
        name
        type
        phase {
          description
          id
          labelOrder
          name
        }
      }
    }
    batchClaimLines(${BATCH_CLAIM_LINE_SORT}) {
      nodes {
        ...batchClaimLineFragment
      }
    }
    ubClaimFindings: auditFindings(
      filter: {
        and: [
          { isOriginal: { equalTo: true } }
          { auditFindingSeedType: { equalTo: UB_CLAIM } }
        ]
      }
    ) {
      nodes {
        ...auditFindingWithReviewFragment
        __typename
      }
      __typename
    }
    ## all audit findings for the claim
    auditFindings {
      nodes {
        ...auditFindingFragment
      }
      totalCount
    }
  }
  ${batchClaimDataFragment}
  ${batchClaimLineFragment}
  ${providerFragment}
  ${auditFindingWithReviewFragment}
  ${auditFindingFragment}
  ${reviewedAuditFindingFragment}
`;

export {
  adminAndAuditorClaimListQueryData,
  adminAndAuditorWorkstationBatchClaimFragment,
  auditFindingFragment,
  auditFindingWithReviewFragment,
  batchClaimState,
  findingV2Fragment,
  payerAndProviderClaimListQueryData,
  payerBatchClaimWorkstationFragment,
  providerBatchClaimWorkstationFragment,
  providerFragment,
  reviewedAuditFindingFragment,
  versionAuditFindingFragment,
};
